import type React from 'react';
import { useState } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type Pdfjs from 'pdfjs-dist';

// Redux
import { useUpdateConversation } from 'components/App/hooks';
import {
  selectConversationId,
  selectIsUploadingAttachment,
  setIsUploadingAttachment,
} from 'reducers/workflows';
import { useAppDispatch, useAppSelector } from 'hooks';

// Request
import { postFileToS3 } from 'api/requests';

//Components
import FormWrapper from 'components/FormWrapper/FormWrapper';
import UserMessage from 'components/UserMessage';
import AddFileForm from './AddFileForm';
import FileItem from './FileItem';

//Types
import type {
  AttachmentAnalysisUploadStepComponent,
  AttachmentUploadStepComponent,
  ZendeskAttachmentUploadStepComponent,
} from 'api/types';
import {
  isAttachmentAnalysisUploadStepComponent,
  isTooManyRequestsError,
} from 'api/types';

//Assets
import WhiteAttachIcn from '../../assets/icons/white-attach-icon.svg';
import { getPresignedUrlToS3, setStepSent } from 'actions/workflows/workflows';
import { CustomFetchError } from 'api/customFetch';
import { sendErrorToSentry } from 'utils/sendErrorToSentry';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface AttachmentUploadStepStepProps {
  isLastStep: boolean;
  stepIndex: number;
  widgetComponent:
    | AttachmentUploadStepComponent
    | AttachmentAnalysisUploadStepComponent
    | ZendeskAttachmentUploadStepComponent;
}

const sanitizeFilename = (filename: string) => {
  const specialCharsRegex = /[~!@#$%^&*()_+`\-=\[\]\{\}|;':",\/<>?]/g;
  return filename.replace(specialCharsRegex, '');
};

const buildSharedComponentFields = (
  widgetComponent:
    | AttachmentUploadStepComponent
    | AttachmentAnalysisUploadStepComponent
    | ZendeskAttachmentUploadStepComponent,
) => {
  const shared = {
    forethoughtText: widgetComponent.component_fields.forethought_text,
  };
  if (isAttachmentAnalysisUploadStepComponent(widgetComponent)) {
    return {
      ...shared,
      maxIndividualSize: null,
      maxTotalSize: 20,
      numFiles: widgetComponent.component_fields.num_files,
      outputVariable: widgetComponent.component_fields.output_variable,
      prompt: widgetComponent.component_fields.prompt,
      required: !widgetComponent.component_fields.allow_skip_attachment,
    };
  }

  return {
    ...shared,
    maxIndividualSize:
      'max_individual_size' in widgetComponent.component_fields
        ? widgetComponent.component_fields.max_individual_size
        : null,
    maxTotalSize:
      'max_total_size' in widgetComponent.component_fields
        ? widgetComponent.component_fields.max_total_size
        : 20,
    numFiles: null,
    outputVariable: null,
    prompt: null,
    required:
      'required' in widgetComponent.component_fields
        ? widgetComponent.component_fields.required
        : false,
  };
};

async function loadPdfjs(files: File[]) {
  if (files.some(file => file.type === 'application/pdf')) {
    const [pdfjsLib] = await Promise.all([
      import('pdfjs-dist'),
      // @ts-expect-error no type definition for pdf.worker.min.mjs
      import('pdfjs-dist/build/pdf.worker.min.mjs'),
    ]);

    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();

    return pdfjsLib;
  }
}

const uploadPDF = async (
  file: File,
  conversationId: string,
  pdfjsLib: typeof Pdfjs | undefined,
): Promise<string[]> => {
  if (!pdfjsLib) {
    throw new Error('Could not load PDF.js');
  }
  // Convert PDF to images
  const pdfData = await file.arrayBuffer();
  const pdfDocument = await pdfjsLib.getDocument({ data: pdfData }).promise;

  const images: File[] = [];
  const pageCount = pdfDocument.numPages;

  for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
    const page = await pdfDocument.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2 }); // Scale can be adjusted

    // Create a canvas and render the PDF page into it
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not get canvas context');
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: context, viewport }).promise;

    // Convert the canvas to a data URL
    const dataUrl = canvas.toDataURL('image/jpeg');

    // Convert the data URL to a Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // Create a File object
    const jpegFile = new File(
      [blob],
      `${file.name.replace(/\.[^/.]+$/, '')}_page_${pageNum}.jpeg`,
      {
        type: 'image/jpeg',
      },
    );
    images.push(jpegFile);
  }

  // Upload each image individually and return their URLs
  const imageUploadPromises: Promise<string>[] = images.map(
    async (image: File): Promise<string> => {
      const { downloadUrl, type, url } = await getPresignedUrlToS3(
        conversationId,
        image.name,
        image.size,
        true,
      );
      await postFileToS3(url, type, image);
      return downloadUrl || '';
    },
  );

  return Promise.all(imageUploadPromises);
};

const AttachmentUploadStep: React.FC<
  React.PropsWithChildren<AttachmentUploadStepStepProps>
> = ({ isLastStep, stepIndex, widgetComponent }) => {
  const {
    forethoughtText,
    maxIndividualSize,
    maxTotalSize,
    numFiles,
    outputVariable,
    prompt,
    required,
  } = buildSharedComponentFields(widgetComponent);

  const dispatch = useAppDispatch();
  const conversationId = useAppSelector(selectConversationId);
  const isUploadingAttachment = useAppSelector(selectIsUploadingAttachment);
  const updateConversation = useUpdateConversation();
  const [files, setFiles] = useState<Array<File> | null>(null);
  const fileSizeLimit = maxTotalSize ?? 20;
  const individualFileSizeLimit = maxIndividualSize ?? fileSizeLimit;
  const isFreshdeskFileSent = widgetComponent.isSent;

  const isFileSizeLimitExceeded = () => {
    const textAnalysisLimit = 20 * 1000;
    const fileSizeMbLimit = fileSizeLimit * 1000000;
    const individualfileSizeMbLimit = individualFileSizeLimit * 1000000;

    if (!files || files.length === 0) {
      return false;
    }

    let totalSize = 0;
    for (const file of files) {
      if (file.size > individualfileSizeMbLimit) {
        return true; // Individual file size limit exceeded
      }
      if (
        file.type === 'text/plain' &&
        file.size > textAnalysisLimit &&
        widgetComponent.component_type === 'attachment_analysis_upload'
      ) {
        return true; // Text file exceeds text analysis limit
      }
      totalSize += file.size;
      if (totalSize > fileSizeMbLimit) {
        return true; // Total file size limit exceeded
      }
    }

    return false; // Neither limit was exceeded
  };

  const handleOnSubmit = async () => {
    dispatch(setIsUploadingAttachment(true));
    const promises = (files ?? []).map(async (file: File) => {
      const { type, url } = await getPresignedUrlToS3(
        conversationId,
        file.name,
        file.size,
      );
      await postFileToS3(url, type, file);
      return url;
    });

    try {
      await Promise.all(promises);
      dispatch(setStepSent({}));
      updateConversation({});
    } catch (error) {
      console.error('Error uploading attachment:', error);
      if (error instanceof CustomFetchError) {
        const errorData = error.data;
        if (isTooManyRequestsError(errorData)) {
          // continue if there are too many requests. The user can no longer upload
          // for this conversation
          dispatch(setStepSent({}));
          updateConversation({});
        }
      }
    }

    dispatch(setIsUploadingAttachment(false));
  };

  const handleOnSubmitOutputVariable = async (outputVariable: string) => {
    dispatch(setIsUploadingAttachment(true));
    const pdfjsLib = await loadPdfjs(files ?? []);

    const promisedAttachments: Promise<string[]>[] = (files ?? []).flatMap(
      async (file: File): Promise<string[]> => {
        // Check if the file is a PDF
        if (file.type === 'application/pdf') {
          return uploadPDF(file, conversationId, pdfjsLib);
        } else {
          // openai vision breaks with special characters
          const sanitizedFileName = sanitizeFilename(file.name);
          // Handle regular file upload
          const { downloadUrl, type, url } = await getPresignedUrlToS3(
            conversationId,
            sanitizedFileName,
            file.size,
            true,
          );

          for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
            const uploadResult = await postFileToS3(url, type, file);

            if (uploadResult.success) {
              return [downloadUrl || ''];
            }

            console.error(
              `Upload failed (attempt ${attempt + 1}):`,
              uploadResult.error,
            );

            sendErrorToSentry({
              additionalContext: {
                attemptNumber: attempt + 1,
                errorMessage: uploadResult.error?.message,
                fileName: file.name,
                fileSize: file.size,
                status: uploadResult.error?.status,
              },
              conversationId,
              error: 'Failed to upload image',
            });

            if (
              uploadResult.error?.status === 400 ||
              uploadResult.error?.status === 403
            ) {
              break;
            }

            if (attempt < MAX_RETRIES - 1) {
              await delay(INITIAL_RETRY_DELAY * 2 ** attempt);
            }
          }
          console.error('Max upload retries reached for file:', file.name);
          return [];
        }
      },
    );

    try {
      // Flatten the array of arrays into a single array of URLs
      const attachments = (await Promise.all(promisedAttachments)).flat();
      dispatch(setStepSent({}));
      updateConversation({ [outputVariable]: attachments });
    } catch (error) {
      console.error('Error uploading attachment:', error);
      if (error instanceof CustomFetchError) {
        const errorData = error.data;
        if (isTooManyRequestsError(errorData)) {
          // continue if there are too many requests. The user can no longer upload
          // for this conversation
          dispatch(setStepSent({}));
          updateConversation({ [outputVariable]: [] });
        }
      }
    }

    // Dispatch and update conversation
    dispatch(setIsUploadingAttachment(false));
  };

  const isError = isFileSizeLimitExceeded();

  // Display the list of sent files as a message
  if (isFreshdeskFileSent) {
    return (
      <UserMessage stepIndex={stepIndex}>
        <Box display='flex' flexDirection='column'>
          {files?.map((file: File, index: number) => (
            <Box
              alignItems='center'
              display='flex'
              key={index}
              marginBottom={index === files.length - 1 ? '0px' : '5px'}
            >
              <WhiteAttachIcon alt='' src={WhiteAttachIcn} />
              <span>{file.name}</span>
            </Box>
          ))}
        </Box>
        {!files && <span dir='auto'>{forethoughtText.skip_attachment}</span>}
      </UserMessage>
    );
  }

  const hasTooManyFiles = numFiles !== null && (files?.length ?? 0) > numFiles;

  // Display the form to upload a file
  return (
    <FormWrapper
      disableSubmitOnEnter
      formTitle={
        widgetComponent.component_type === 'attachment_analysis_upload'
          ? prompt
          : forethoughtText.form_title
      }
      isDisabled={
        isUploadingAttachment ||
        !files ||
        files?.length === 0 ||
        isError ||
        hasTooManyFiles
      }
      isLastStep={isLastStep}
      onSubmit={(e: React.SyntheticEvent) => {
        e.preventDefault();
        if (outputVariable) {
          handleOnSubmitOutputVariable(outputVariable);
          return;
        }
        handleOnSubmit();
      }}
      stepIndex={stepIndex}
      submitButtonText={forethoughtText.submit_button}
    >
      <AddFileForm
        acceptableFileTypes={
          widgetComponent.component_type === 'attachment_analysis_upload'
            ? 'image/png, image/jpeg, image/webp, image/gif, .txt, .pdf'
            : undefined
        }
        componentText={forethoughtText}
        fileSizeLimit={fileSizeLimit}
        hasTooManyFiles={hasTooManyFiles}
        isError={isError}
        numFiles={numFiles}
        required={required}
        setFiles={setFiles}
      />
      {files?.map((file: File, fileIndex: number) => (
        <FileItem
          file={file}
          fileIndex={fileIndex}
          files={files}
          key={fileIndex}
          setFiles={setFiles}
        />
      ))}
    </FormWrapper>
  );
};

export default AttachmentUploadStep;

const WhiteAttachIcon = styled('img')`
  margin-right: 5px;
`;
